export const ALTA_VS_TITLE_DISPLAY_NAME = 'ALTA vs Title Comparison'
export const TENANT_IMPROVEMENT_DISPLAY_NAME = 'Tenant Improvement Plan Check'

// The backend CheckRegistry's DisplayName for the admin pre-screen. Lives
// here because two places key on it: useCheckStageProgress's admin-only /
// exclude-admin filter, and useInFlightAiCheckSubmittal deciding whether a
// permit has an admin check still running.
export const ADMIN_CHECK_DISPLAY_NAME = 'Admin Check'

export const WHOLE_SUBMITTAL_QC_DISPLAY_NAMES = new Set([
  ALTA_VS_TITLE_DISPLAY_NAME,
  TENANT_IMPROVEMENT_DISPLAY_NAME,
])

export const normalizeCheckDisplayName = (
  name: string | undefined | null,
): string => (name ?? '').trim().toLowerCase()

export const isAltaVsTitleDisplayName = (
  name: string | undefined | null,
): boolean =>
  normalizeCheckDisplayName(name) ===
  normalizeCheckDisplayName(ALTA_VS_TITLE_DISPLAY_NAME)

export const isAdminCheckDisplayName = (
  name: string | undefined | null,
): boolean =>
  normalizeCheckDisplayName(name) ===
  normalizeCheckDisplayName(ADMIN_CHECK_DISPLAY_NAME)

/**
 * Whether a check belongs to the ADMIN checklist, given both things that can
 * say so.
 *
 * Use this rather than isAdminCheckDisplayName wherever a CheckProgressPayload
 * is available. The display name cannot answer it for a rerun: a single-document
 * rerun runs PermitSubmittalRecognitionCheck and reports "Permit Submittal
 * Recognition" whether it was launched from the Administrative Check page or
 * from First Submittal Review. checklistScope is the field that names the
 * checklist a scoped dispatch narrowed itself to, and it wins when present.
 *
 * Getting this wrong is not cosmetic and has bitten three separate call sites:
 * an admin rerun read as heavy disables the wrong report link, is dropped by an
 * "admin-only" progress filter (so its page shows no progress at all), and is
 * folded into the heavy page as if it were heavy work.
 *
 * A null/absent scope means "full run", where the display name IS the
 * discriminator.
 */
export const isAdminCheckWork = (check: {
  displayName?: string | null
  checklistScope?: string | null
}): boolean =>
  check.checklistScope
    ? check.checklistScope === 'AdminCheck'
    : isAdminCheckDisplayName(check.displayName)

export const isWholeSubmittalQcDisplayName = (
  name: string | undefined | null,
): boolean => {
  const normalized = normalizeCheckDisplayName(name)
  return [...WHOLE_SUBMITTAL_QC_DISPLAY_NAMES].some(
    (displayName) => normalizeCheckDisplayName(displayName) === normalized,
  )
}

const ALTA_OR_TITLE_KINDS = new Set(['alta', 'title'])

export const isAltaOrTitleKind = (kind: string | null | undefined): boolean =>
  ALTA_OR_TITLE_KINDS.has(kind ?? '')

export const hasOnlyAltaOrTitleKinds = (
  kinds: Iterable<string | null | undefined>,
): boolean => {
  let sawAny = false
  for (const kind of kinds) {
    if (!kind) continue
    sawAny = true
    if (!isAltaOrTitleKind(kind)) return false
  }
  return sawAny
}

export const deriveExpectedQcChecksFromKinds = (
  kinds: Iterable<string | null | undefined>,
): string[] => {
  const kindSet = new Set(
    [...kinds].filter((kind): kind is string => Boolean(kind)),
  )
  const expected: string[] = []
  if (kindSet.has('alta') && kindSet.has('title')) {
    expected.push(ALTA_VS_TITLE_DISPLAY_NAME)
  }
  if (kindSet.has('ti')) {
    expected.push(TENANT_IMPROVEMENT_DISPLAY_NAME)
  }
  return expected
}
